/**
 * Vendor's billing-model card (Iter 62).
 *
 * Shows:
 *  - Current billing model (admin-controlled)
 *  - Subscription lifecycle (active / past_due / suspended / manual) — only for monthly plan
 *  - Pending switch request (if any) — vendor can cancel it
 *  - "Request switch" CTA → goes to admin approval queue
 *  - Recent subscription invoices for monthly-plan vendors
 *  - Stripe billing portal link (opens if real Stripe is configured)
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wallet, Calendar, ArrowRightLeft, Info, AlertTriangle, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function _fmtCycle(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function _modeLabel(mode, value) {
  if (mode === "monthly_per_car") return `Monthly · €${value ?? "—"}/vehicle`;
  if (mode === "percent") return `Commission · ${value ?? "—"}% per booking`;
  return "Platform default";
}

function _statusBadge(status) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold" data-testid="vendor-billing-status-active">
        <CheckCircle2 className="h-3 w-3" /> Subscription active
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold" data-testid="vendor-billing-status-past-due">
        <AlertTriangle className="h-3 w-3" /> Payment past due
      </span>
    );
  }
  if (status === "suspended") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-semibold" data-testid="vendor-billing-status-suspended">
        <XCircle className="h-3 w-3" /> Listings suspended
      </span>
    );
  }
  if (status === "manual") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-semibold" data-testid="vendor-billing-status-manual">
        <Info className="h-3 w-3" /> Manual billing
      </span>
    );
  }
  return null;
}

export default function VendorBillingCard() {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSwitchForm, setShowSwitchForm] = useState(false);
  const [switchNote, setSwitchNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/vendor/billing");
      setData(data);
      if (data.mode === "monthly_per_car") {
        try {
          const { data: invs } = await api.get("/vendor/billing/invoices");
          setInvoices(invs || []);
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      console.error("billing load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const currentMode = data.mode === "default" ? "percent" : data.mode;
  const switchTo = currentMode === "monthly_per_car" ? "percent" : "monthly_per_car";
  const switchLabel = switchTo === "monthly_per_car" ? "Request switch to monthly per vehicle" : "Request switch to commission per booking";
  const pending = data.pending_switch_request;

  const submitRequest = async () => {
    setBusy(true);
    try {
      await api.post("/vendor/billing/switch-request", { requested_mode: switchTo, customer_note: switchNote.trim() || null });
      toast.success("Switch request sent — an admin will review it");
      setShowSwitchForm(false);
      setSwitchNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await api.delete(`/vendor/billing/switch-request/${pending.id}`);
      toast.success("Switch request cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/vendor/billing/portal", {});
      if (r?.url) window.open(r.url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-4 sm:p-5 shadow-sm" data-testid="vendor-billing-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Wallet className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg">Billing model</h2>
        {data.mode === "monthly_per_car" && _statusBadge(data.subscription_status)}
      </div>

      {data.subscription_status === "suspended" && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2.5 text-xs text-rose-800 mb-3 flex items-start gap-2" data-testid="vendor-billing-suspended-banner">
          <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Your subscription has been suspended for non-payment. Your listings are <strong>hidden from the public</strong>.
            Settle the outstanding invoice with admin to restore visibility.
          </span>
        </div>
      )}
      {data.subscription_status === "past_due" && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 mb-3 flex items-start gap-2" data-testid="vendor-billing-past-due-banner">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Your last subscription invoice failed. Settle it before the grace period ends to keep your listings live.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5" data-testid="vendor-billing-current">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Current</div>
          <div className="font-semibold text-sm">{_modeLabel(data.mode, data.value)}</div>
          {data.mode === "monthly_per_car" && data.reduced_percent > 0 && (
            <div className="text-[11px] text-neutral-500">+ {data.reduced_percent}% per booking</div>
          )}
        </div>
        <div className="rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5" data-testid="vendor-billing-effective">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Effective % per booking</div>
          <div className="font-semibold text-sm">{Number(data.effective_percent || 0).toFixed(1)}%</div>
        </div>
        <div className="rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5" data-testid="vendor-billing-monthly-due">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" />Monthly fee est.
          </div>
          <div className="font-semibold text-sm">
            {data.mode === "monthly_per_car" ? `€${Number(data.monthly_fee_estimate || 0).toFixed(2)}` : "—"}
          </div>
        </div>
      </div>

      {/* Iter 62 P2: monthly-fee preview breakdown — surface the math so vendors know exactly
          how their bill scales with their fleet. Only shown on the monthly plan. */}
      {data.mode === "monthly_per_car" && data.value && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-3 mb-3" data-testid="vendor-billing-fee-preview">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1.5">This month's bill</div>
          <div className="flex items-baseline gap-2 text-sm flex-wrap">
            <span className="font-display text-blue-900 text-lg font-semibold">€{Number(data.value).toFixed(2)}</span>
            <span className="text-blue-700">×</span>
            <span className="font-display text-blue-900 text-lg font-semibold" data-testid="vendor-billing-active-car-count">{data.active_car_count ?? 0}</span>
            <span className="text-blue-700 text-xs">active car{(data.active_car_count ?? 0) === 1 ? "" : "s"}</span>
            <span className="text-blue-700 ml-auto">=</span>
            <span className="font-display text-blue-900 text-xl font-bold" data-testid="vendor-billing-monthly-total">€{Number(data.monthly_fee_estimate || 0).toFixed(2)}</span>
          </div>
          <div className="text-[10px] text-blue-700 mt-1">
            Add a car → next month's bill goes up by €{Number(data.value).toFixed(2)}.
            Pause a car → next month's bill goes down by €{Number(data.value).toFixed(2)}.
          </div>
        </div>
      )}

      {pending && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-900 mb-3 flex items-start gap-2 justify-between" data-testid="vendor-billing-pending-request">
          <div className="flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Pending admin review: switch to <strong>{_modeLabel(pending.requested_mode, "—")}</strong>.
              {pending.customer_note && <em className="block mt-1 opacity-70">"{pending.customer_note}"</em>}
            </span>
          </div>
          <button
            type="button"
            onClick={cancelRequest}
            disabled={busy}
            className="text-[11px] underline hover:no-underline shrink-0"
            data-testid="vendor-billing-cancel-request"
          >
            Cancel
          </button>
        </div>
      )}

      {!pending && !showSwitchForm && (
        <Button
          onClick={() => setShowSwitchForm(true)}
          disabled={busy}
          className="rounded-full bg-blue-600 hover:bg-blue-700"
          data-testid="vendor-billing-open-switch-form"
        >
          <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />{switchLabel}
        </Button>
      )}

      {!pending && showSwitchForm && (
        <div className="rounded-xl border border-border bg-neutral-50/40 p-3 mb-2" data-testid="vendor-billing-switch-form">
          <label className="block text-[11px] font-semibold text-neutral-600 mb-1">
            Tell admin why you'd like to switch (optional)
          </label>
          <textarea
            value={switchNote}
            onChange={(e) => setSwitchNote(e.target.value)}
            placeholder="e.g. I'd like a flat predictable fee instead of variable commission."
            className="w-full rounded-lg border border-border px-2.5 py-1.5 text-sm bg-white"
            rows={2}
            maxLength={500}
            data-testid="vendor-billing-switch-note"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              onClick={submitRequest}
              disabled={busy}
              size="sm"
              className="rounded-full bg-blue-600 hover:bg-blue-700"
              data-testid="vendor-billing-submit-request"
            >
              Send to admin
            </Button>
            <Button
              onClick={() => { setShowSwitchForm(false); setSwitchNote(""); }}
              disabled={busy}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="vendor-billing-cancel-switch-form"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {data.mode === "monthly_per_car" && data.stripe_subscription_id && (
        <Button
          onClick={openPortal}
          disabled={busy}
          variant="outline"
          size="sm"
          className="rounded-full mt-2"
          data-testid="vendor-billing-open-portal"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Manage payment method
        </Button>
      )}

      {data.mode === "monthly_per_car" && invoices.length > 0 && (
        <div className="mt-4" data-testid="vendor-billing-invoices">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Recent invoices</div>
          <ul className="divide-y divide-border rounded-xl border border-border bg-white">
            {invoices.slice(0, 5).map((inv) => (
              <li key={inv.id} className="flex items-center justify-between px-3 py-2 text-xs">
                <div>
                  <div className="font-semibold">€{Number(inv.amount).toFixed(2)}</div>
                  <div className="text-neutral-500">{new Date(inv.created_at).toLocaleDateString("en-GB")}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : inv.status === "failed" ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : "bg-neutral-50 text-neutral-600 border border-border"
                }`}>{inv.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[11px] text-neutral-500 mt-3">
        Your admin-set rate doesn't change — only which billing model you're on. Switch requests go to the admin for review;
        once approved, the new model applies to any new bookings going forward.
      </p>
    </div>
  );
}
